import React from 'react';
import { compose } from 'recompose';
import styled, { StylesProps, cx, useStyles } from '../../../../../lib/styles';
import { Article } from '../../../../../models/article';
import heroTilesStyles from './HeroTilesBlock_styles';
import Icon from '../../../../../components/presentational/Icon';
import { HEROBLOCK_AUTHOR_CLICKED, HEROBLOCK_CATEGORY_CLICKED, HEROBLOCK_FEATURED_AUTHOR_CLICKED, HEROBLOCK_FEATURED_CATEGORY_CLICKED, HEROBLOCK_FEATURED_TITLE_CLICKED, HEROBLOCK_TITLE_CLICKED, raiseEvent } from '../../../../../lib/gtm';
import _ from 'lodash';

export interface HeroTilesBlockData {
  type: 'hero_tiles_block';
  articles: Article[];
}

interface HeroTilesBlockProps {
  block: HeroTilesBlockData;
}

interface FeaturedTileBlockProps {
  featuredArticle: Article;
  featuredImageWidth?: number;
  styles?: {};
}

export const FeaturedTileBlock = ({
  featuredArticle,
  featuredImageWidth = 600,
  styles = {},
}: FeaturedTileBlockProps) => {
  // console.log('FeaturedTileBlock', {featuredArticle, featuredImageWidth, styles});
  const heroTileStyles = useStyles(heroTilesStyles());
  const articleImage = featuredArticle.large_image ?? process.env.ARTICLE_FALLBACK_FEATURE_IMAGE;

  return (
    <div
      className={cx(
        `featuredTileCard lazy-load-bgimage`,
        heroTileStyles.tileCard,
        heroTileStyles.featuredTileCard
      )}
      data-bgimage={`${articleImage}?w=${featuredImageWidth}`}
      style={{
        ...styles,
      }}
    >
      <div
        className={cx(
          'tileContentContainer',
          heroTileStyles.tileContentContainer
        )}
      >
        <div
          className={cx(
            'featuredTileTitle',
            heroTileStyles.tileTitle,
            heroTileStyles.featuredTileTitle
          )}
        >
          <a 
            href={featuredArticle.link} 
            className={cx(
              'tileLink',
              heroTileStyles.tileLink,
              heroTileStyles.tileTitleLink,
              heroTileStyles.featuredTileTitleLink
            )}
            onClick={() => raiseEvent(HEROBLOCK_FEATURED_TITLE_CLICKED)}
            >
            {featuredArticle.title}
          </a>
        </div>
        <div
          className={cx(
            'tileMetaContainer',
            heroTileStyles.tileMetaContainer,
            heroTileStyles.featuredTileMetaContainer
          )}
        >
          <div className={cx(`tileMeta`, heroTileStyles.tileMeta)}>
            <div
              className={cx(
                'authorMeta',
                heroTileStyles.authorMeta,
                heroTileStyles.featuredTileAuthorMeta
              )}
            >
              <img
                src={featuredArticle.author.avatar_24}
                className={`tileAuthor-avatar`}
                alt={`${featuredArticle.author.name} avatar`}
              />
              <span
                className={cx(
                  `tileAuthor-name mr-3`,
                  heroTileStyles.tileAuthor
                )}
              >
                <a
                  href={featuredArticle.author.link}
                  className={cx(
                    'tileLink',
                    heroTileStyles.tileLink
                  )}
                  onClick={() => raiseEvent(HEROBLOCK_FEATURED_AUTHOR_CLICKED)}
                >
                  {featuredArticle.author.name}
                </a>
              </span>
            </div>
            <a
              className={cx(
                `tileCategory featuredTileCategory`,
                heroTileStyles.tileCategory,
                heroTileStyles.featuredTileCategory
              )}
              href={featuredArticle.category_link}
              onClick={() => raiseEvent(HEROBLOCK_FEATURED_CATEGORY_CLICKED)}
            >
              <div>
                {featuredArticle.category}
              </div>
              <Icon icon="chevron_right" className={cx(`ml-1`)} />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

const HeroTilesBlock = ({
  block: { articles },
  styles,
}: HeroTilesBlockProps & StylesProps<typeof heroTilesStyles>) => {
  const featuredArticle: Article = articles[0];
  const otherTiles: Article[] = articles.slice(1);
  return (
    <div
      className={cx(
        `d-flex flex-nowrap heroTilesBlock`,
        styles.container,
        styles.heroTilesBlock
      )}
    >
      <FeaturedTileBlock featuredArticle={featuredArticle} />
      <div className={cx('d-flex flex-wrap tileBlocks', styles.tileBlocks)}>
        {otherTiles.map((article, i) => (
          <div
            key={i}
            className={cx(`tileCard lazy-load-bgimage`, styles.tileCard)}
            data-bgimage={`${article.large_image}?w=600`}
            style={{
              backgroundSize: `cover`,
              backgroundPosition: `center`,
            }}
          >
            <div
              className={cx(
                'tileContentContainer',
                styles.tileContentContainer
              )}
            >
              <div className={cx('tileTitle', styles.tileTitle)}>
                <a 
                  href={article.link} 
                  className={cx(
                    'tileLink',
                    styles.tileLink,
                    styles.tileTitleLink
                  )}
                  onClick={() => raiseEvent(HEROBLOCK_TITLE_CLICKED)}
                >
                  {article.title}
                </a>
              </div>
              <div
                className={cx('tileMetaContainer', styles.tileMetaContainer)}
              >
                <div className={cx(`tileMeta`, styles.tileMeta)}>
                  <div className={cx('authorMeta', styles.authorMeta)}>
                    <img
                      src={article.author.avatar_24}
                      className={`tileAuthor-avatar`}
                      alt={`${article.author.name} avatar`}
                    />
                    <span
                      className={cx(`tileAuthor-name mr-3`, styles.tileAuthor)}
                    >
                      <a 
                        href={article.author.link} 
                        className={cx(
                          'tileLink',
                          styles.tileLink
                        )}
                        onClick={() => raiseEvent(HEROBLOCK_AUTHOR_CLICKED)}
                      >
                        {article.author.name}
                      </a>
                    </span>
                  </div>
                  <a
                    className={cx(
                      `tileCategory`, 
                      styles.tileCategory
                    )}
                    href={article.category_link}
                    onClick={() => raiseEvent(HEROBLOCK_CATEGORY_CLICKED)}
                  >
                    <div>
                      {article.category}
                    </div>
                    <Icon icon="chevron_right" className={cx(`ml-1`)} />
                  </a>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default compose(styled(heroTilesStyles))(HeroTilesBlock);
